<script setup>
import { useTemplateRef, onMounted, ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { mocGioNgay } from 'shared/helpers/timeHelper';
import { useToggle } from '@vueuse/core';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  note: {
    type: Object,
    required: true,
  },
  writtenBy: {
    type: String,
    required: true,
  },
  allowDelete: {
    type: Boolean,
    default: false,
  },
  // BonBoZ: sửa tại chỗ và ghim. Tắt mặc định để trang danh bạ giữ nguyên hành
  // vi cũ; chỉ sidebar hội thoại bật lên.
  allowEdit: {
    type: Boolean,
    default: false,
  },
  allowPin: {
    type: Boolean,
    default: false,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  collapsible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['delete', 'update', 'togglePin']);
const noteContentRef = useTemplateRef('noteContentRef');
const editorRef = useTemplateRef('editorRef');
const needsCollapse = ref(false);
const [isExpanded, toggleExpanded] = useToggle();
const { t } = useI18n();
const { formatMessage } = useMessageFormatter();

const isEditing = ref(false);
const draft = ref('');

const isPinned = computed(() => Boolean(props.note.pinnedAt));

const handleDelete = () => {
  emit('delete', props.note.id);
};

const togglePin = () => {
  emit('togglePin', { id: props.note.id, pinned: !isPinned.value });
};

const startEditing = async () => {
  // Sửa trên văn bản thô: nội dung lưu ở dạng markdown, còn phần hiển thị đã
  // qua `formatMessage` thành HTML. Đưa HTML vào ô sửa là lưu lại lần nữa thì
  // dấu markdown nhân đôi.
  draft.value = props.note.content || '';
  isEditing.value = true;
  await nextTick();
  editorRef.value?.focus();
};

const cancelEditing = () => {
  isEditing.value = false;
  draft.value = '';
};

const saveEdit = () => {
  const content = draft.value.trim();
  if (!content || content === props.note.content) {
    cancelEditing();
    return;
  }
  emit('update', { id: props.note.id, content });
  isEditing.value = false;
};

// Enter lưu, Shift+Enter xuống dòng. `isComposing` chặn lúc bộ gõ tiếng Việt
// đang dựng chữ — không có nó thì gõ "chuẩn" bị lưu ngay ở chữ "chuân".
const onKeydown = event => {
  if (event.key === 'Escape') {
    cancelEditing();
    return;
  }
  if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
  event.preventDefault();
  saveEdit();
};

onMounted(() => {
  if (props.collapsible) {
    // Check if content height exceeds approximately 4 lines
    // Assuming line height is ~1.625 and font size is ~14px
    const threshold = 14 * 1.625 * 4; // ~84px
    needsCollapse.value = noteContentRef.value?.clientHeight > threshold;
  }
});
</script>

<template>
  <div
    class="flex flex-col gap-2 border-b border-n-strong group/note"
    :class="{ 'bg-n-amber-2 dark:bg-n-amber-2': isPinned }"
  >
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-1.5 min-w-0">
        <!-- Ghim phải nhìn ra ngay cả khi không rê chuột, nên là icon cố định
             chứ không phải nút ẩn như sửa/xóa. -->
        <span
          v-if="isPinned"
          v-tooltip="
            note.pinnedBy?.name
              ? t('BONBOZ.SIDEBAR.GHI_CHU_GHIM_BOI', {
                  ten: note.pinnedBy.name,
                })
              : t('BONBOZ.SIDEBAR.GHI_CHU_DA_GHIM')
          "
          class="i-lucide-pin size-3.5 shrink-0 text-n-amber-11"
          aria-hidden="true"
        />
        <Avatar
          :name="note?.user?.name || 'Bot'"
          :src="
            note?.user?.name
              ? note?.user?.thumbnail
              : '/assets/images/chatwoot_bot.png'
          "
          :size="16"
          rounded-full
        />
        <div class="min-w-0 truncate">
          <span class="inline-flex items-center gap-1 text-sm text-n-slate-11">
            <span class="font-medium text-n-slate-12">{{ writtenBy }}</span>
            {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.WROTE') }}
            <span class="font-medium text-n-slate-12">
              {{ mocGioNgay(note.createdAt) }}
            </span>
            <span
              v-if="note.updatedAt > note.createdAt"
              class="text-n-slate-10"
            >
              · {{ t('BONBOZ.SIDEBAR.GHI_CHU_DA_SUA') }}
            </span>
          </span>
        </div>
      </div>
      <div v-if="!isEditing" class="flex items-center gap-1 shrink-0">
        <Button
          v-if="allowPin"
          v-tooltip="
            isPinned
              ? t('BONBOZ.SIDEBAR.GHI_CHU_BO_GHIM')
              : t('BONBOZ.SIDEBAR.GHI_CHU_GHIM')
          "
          variant="faded"
          :color="isPinned ? 'amber' : 'slate'"
          size="xs"
          :icon="isPinned ? 'i-lucide-pin-off' : 'i-lucide-pin'"
          :disabled="isSaving"
          :class="isPinned ? '' : 'opacity-0 group-hover/note:opacity-100'"
          @click="togglePin"
        />
        <Button
          v-if="allowEdit"
          v-tooltip="t('BONBOZ.SIDEBAR.GHI_CHU_SUA')"
          variant="faded"
          color="slate"
          size="xs"
          icon="i-lucide-pencil"
          :disabled="isSaving"
          class="opacity-0 group-hover/note:opacity-100"
          @click="startEditing"
        />
        <Button
          v-if="allowDelete"
          v-tooltip="t('BONBOZ.SIDEBAR.GHI_CHU_XOA')"
          variant="faded"
          color="ruby"
          size="xs"
          icon="i-lucide-trash"
          :disabled="isSaving"
          class="opacity-0 group-hover/note:opacity-100"
          @click="handleDelete"
        />
      </div>
    </div>

    <div v-if="isEditing" class="flex flex-col gap-2">
      <textarea
        ref="editorRef"
        v-model="draft"
        rows="3"
        class="w-full px-2 py-1.5 text-sm rounded-lg resize-y bg-n-alpha-2 text-n-slate-12 border border-n-weak focus:outline-none focus:border-n-brand"
        @keydown="onKeydown"
      />
      <div class="flex items-center justify-end gap-2">
        <Button
          variant="faded"
          color="slate"
          size="xs"
          :label="t('BONBOZ.SIDEBAR.GHI_CHU_HUY')"
          @click="cancelEditing"
        />
        <Button
          color="blue"
          size="xs"
          :label="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.SAVE')"
          :is-loading="isSaving"
          :disabled="!draft.trim()"
          @click="saveEdit"
        />
      </div>
    </div>

    <template v-else>
      <p
        ref="noteContentRef"
        v-dompurify-html="formatMessage(note.content || '')"
        class="mb-0 prose-sm prose-p:text-sm prose-p:leading-relaxed prose-p:mb-1 prose-p:mt-0 prose-ul:mb-1 prose-ul:mt-0 text-n-slate-12"
        :class="{
          'line-clamp-4': collapsible && !isExpanded && needsCollapse,
        }"
      />
      <p v-if="collapsible && needsCollapse">
        <Button
          variant="faded"
          color="blue"
          size="xs"
          :icon="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          @click="() => toggleExpanded()"
        >
          <template v-if="isExpanded">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.COLLAPSE') }}
          </template>
          <template v-else>
            {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.EXPAND') }}
          </template>
        </Button>
      </p>
    </template>
  </div>
</template>
